import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, Sparkles, CheckCircle, Mail, ArrowRight, Clock, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';

const STEPS = [
    {
        n: '01',
        icon: Upload,
        title: 'Uploadez le PDF du mandat signé',
        desc: 'Glissez le PDF déjà signé chez votre CRM dans Homly. Aucun changement de workflow.',
        color: '#FF914D',
        time: '5 sec',
    },
    {
        n: '02',
        icon: Sparkles,
        title: 'L\'IA extrait tous les champs',
        desc: 'Numéro de mandat, vendeur, adresse, prix, honoraires, type, durée… 100% automatique.',
        color: '#681793',
        time: '5 sec',
    },
    {
        n: '03',
        icon: CheckCircle,
        title: 'Vous validez en 1 coup d\'œil',
        desc: 'Tous les champs pré-remplis, vous corrigez seulement si besoin. Mandat créé en statut actif.',
        color: '#10b981',
        time: '10 sec',
    },
    {
        n: '04',
        icon: Mail,
        title: 'Le vendeur reçoit son invitation par email',
        desc: 'Il active son espace en 1 clic et suit visites, offres et activité en temps réel.',
        color: '#3b82f6',
        time: '10 sec',
    },
];

export default function QuickstartPage() {
    return (
        <div
            className="min-h-screen bg-[#0a0a14] text-white"
            data-testid="quickstart-page"
        >
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{
                    background: 'radial-gradient(800px circle at 20% 0%, #681793 0%, transparent 50%), radial-gradient(800px circle at 80% 50%, #FF914D 0%, transparent 50%)'
                }} />
                <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-12">
                    <div className="flex items-center gap-3 mb-8">
                        <Link to="/" className="flex items-center gap-2 group">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center font-black text-lg">
                                H
                            </div>
                            <span className="font-bold text-xl">Homly</span>
                        </Link>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF914D]/10 border border-[#FF914D]/30 text-[#FF914D] text-xs font-semibold mb-6">
                            <Zap className="w-3.5 h-3.5" />
                            DÉMARRAGE EXPRESS · 30 SECONDES
                        </div>
                        <h1 className="text-5xl md:text-6xl font-black tracking-tight leading-[1.05] mb-6">
                            Vos vendeurs connectés
                            <br />
                            <span className="bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">
                                en moins de 30 secondes
                            </span>
                        </h1>
                        <p className="text-xl text-slate-300 max-w-2xl leading-relaxed">
                            Aucune saisie. Aucun apprentissage. Vous uploadez le PDF du mandat signé,
                            l'IA extrait tout, et votre vendeur reçoit son espace de suivi par email.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* GIF Demo */}
            <section className="max-w-5xl mx-auto px-6 pb-12">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-[#681793]/20 bg-black"
                    data-testid="quickstart-demo"
                >
                    <img
                        src="/quickstart-demo.gif"
                        alt="Démo : Upload PDF → IA → Validation → Email vendeur"
                        className="w-full h-auto block"
                    />
                </motion.div>
                <p className="text-center text-sm text-slate-400 mt-4 flex items-center justify-center gap-2">
                    <Clock className="w-3.5 h-3.5" />
                    Temps moyen constaté chez nos premières agences : <strong className="text-white">28 secondes</strong>
                </p>
            </section>

            {/* Steps */}
            <section className="max-w-5xl mx-auto px-6 py-16">
                <h2 className="text-3xl md:text-4xl font-black mb-12 text-center">
                    Comment ça marche, étape par étape
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                    {STEPS.map((step, idx) => (
                        <motion.div
                            key={step.n}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                            className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] transition-colors"
                            data-testid={`quickstart-step-${idx + 1}`}
                        >
                            <div className="flex items-start gap-4">
                                <div
                                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                                    style={{ background: `${step.color}20`, border: `1px solid ${step.color}40` }}
                                >
                                    <step.icon className="w-6 h-6" style={{ color: step.color }} />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="text-xs font-mono text-slate-500">{step.n}</span>
                                        <span
                                            className="text-xs font-bold px-2 py-0.5 rounded-full"
                                            style={{ background: `${step.color}20`, color: step.color }}
                                        >
                                            {step.time}
                                        </span>
                                    </div>
                                    <h3 className="font-bold text-lg mb-1.5">{step.title}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-3xl mx-auto px-6 py-16 text-center">
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#681793]/10 via-transparent to-[#FF914D]/10 p-10"
                >
                    <h2 className="text-3xl md:text-4xl font-black mb-4">
                        Prêt à uploader votre 1er mandat ?
                    </h2>
                    <p className="text-slate-300 mb-8">
                        Connectez-vous, ouvrez Homly, glissez un PDF. C'est tout.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                        <Link to="/admin/mandates/upload">
                            <Button
                                className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white h-12 px-8 text-base font-semibold"
                                data-testid="quickstart-cta-upload"
                            >
                                Uploader mon 1er mandat
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button
                                variant="outline"
                                className="h-12 px-8 text-base font-semibold border-white/20"
                                data-testid="quickstart-cta-login"
                            >
                                Me connecter
                            </Button>
                        </Link>
                    </div>
                </motion.div>
            </section>

            <footer className="text-center py-8 text-xs text-slate-500">
                Homly © 2026 — L'immobilier connecté
            </footer>
        </div>
    );
}
